// Framework dependencies
import React, { useEffect, useState } from 'react';

// Repository dependencies
import { useNormalizedAddressQuery } from '@updater/ui-widgets';
import { formatRESTAddress } from 'utils/addresses';
import { useGetUserInfoQuery } from 'flows/core/hooks/use-get-user-info-query';

// Local dependencies
import { Address, BooleanString } from '../types';
import { useForwardMailUserDataStore } from '../store';
import { fetchForwardMails } from '../utils';
import { useFYMBaseDataStore } from '../store/base-data-store';
import { addDays, addMonths, format } from 'date-fns';
/**
 * @method DataProvider
 * @description Data Provider to each Flow page
 * @returns {React.FC}
 */
export const DataProvider = ({ children }) => {
  const [loading, setLoadingState] = useState(true);
  const [loadingGQL, setLoadingGQL] = useState(true);
  const [noFilterFormData, setNoFilterFormData] = useState([]);
  const { getResults } = fetchForwardMails();
  const { setUserData, ...userData } = useForwardMailUserDataStore();
  const { resetBaseData, setBaseData, formData } = useFYMBaseDataStore();
  useEffect(() => {
    resetBaseData();
  }, [resetBaseData]);
  const [normalizeAddress] = useNormalizedAddressQuery();

  // GQL call
  const {
    loading: queryLoading,
    data: gqlData,
    error: gqlError,
  } = useGetUserInfoQuery();

  /**
   * @method normalizeAddresses
   * @description check addresses to see if they are normalized and call callback function with orignal and normalized values
   * @param {Array[Address]} addresses input addresses array to be normalized
   * @param {function} callback callback function
   * @returns {Array[TNormalizedAddress]} props.normalizedAddresses - normalized addresses or null
   * @returns {Array[Address]} props.addresses input addresses array
   * @returns {boolean} props.isNormalized - value that summarized if all addresses are normalized or not
   */
  const normalizeAddresses = async (
    addresses: Address[],
    callback: (value) => void
  ) => {
    const normalizationResults = addresses.map((address) => {
      return normalizeAddress({
        variables: {
          input: {
            city: address.city,
            street: address.street,
            postalCode: address.postalCode,
            state: address.state,
            unit: address.unit,
          },
        },
      });
    });
    Promise.all(normalizationResults)
      .then((values) => {
        return {
          normalizedAddresses: values,
          addresses,
          isNormalized: values.reduce((previousValue, currentValue) => {
            return (
              !!currentValue?.data?.normalizedAddress?.normalizedAddress &&
              currentValue?.data?.normalizedAddress?.normalizedAddress
                ?.classification !== 'COMMERCIAL' &&
              currentValue?.data?.normalizedAddress?.normalizedAddress
                ?.classification !== 'MILITARY' &&
              currentValue?.data?.normalizedAddress?.normalizedAddress
                ?.status !== 'INVALID' &&
              currentValue?.data?.normalizedAddress?.normalizedAddress
                ?.status !== 'APT_NEEDED' &&
              previousValue
            );
          }, true),
        };
      })
      .then((results) => callback(results));
  };

  /**
   * @method setAddressesCallback
   * @description set userData with normalized addresses and final considerations
   * @param  {Array[TNormalizedAddress]} props.normalizedAddresses - normalized addresses or null
   * @param {Array[Address]} props.addresses input addresses array
   * @param {boolean} props.isNormalized - value that summarized if all addresses are normalized or not
   */
  const setAddressesCallback = ({
    addresses,
    normalizedAddresses,
    isNormalized,
  }) => {
    setUserData({
      currentAddress: {
        ...addresses[0],
        unit: addresses[0].unit || '',
        normalizationResponse:
          normalizedAddresses[0]?.data?.normalizedAddress?.normalizedAddress
            ?.components || null,
      },
      newAddress: {
        ...addresses[1],
        unit: addresses[1].unit || '',
        normalizationResponse:
          normalizedAddresses[1]?.data?.normalizedAddress?.normalizedAddress
            ?.components || null,
      },
    });

    if (formData?.[0]?.move.need_verify !== false) {
      setUserData({
        addressesNormalized: isNormalized.toString(),
      });
    }

    setLoadingGQL(false);
  };

  // set loading application
  useEffect(() => {
    setBaseData({ loading: loading || loadingGQL });
  }, [loading, loadingGQL, setBaseData]);

  // get async REST api results
  useEffect(() => {
    if (loading) {
      const fetchData = async () => {
        try {
          const apiResults = await getResults();
          setNoFilterFormData(apiResults);
          setLoadingState(false);
        } catch (apiError) {
          setBaseData({ error: apiError });
          setLoadingState(false);
        }
      };
      fetchData();
    }
  }, [getResults, loading, setBaseData, setNoFilterFormData]);

  // main data loading logic
  useEffect(() => {
    if (!queryLoading) {
      if (gqlError) {
        setLoadingGQL(false);
        setBaseData({ error: gqlError });
      } else if (!loading) {
        // first get GQL data
        const {
          getUser: {
            user: {
              firstName,
              lastName,
              phone,
              currentMove: { id: moveId, date, fromAddress, toAddress },
            },
          },
        } = gqlData;

        // First filter form data per current move and discard the rest
        const filteredFormData = noFilterFormData.filter(
          (fymForm) => fymForm?.move_id.toString() === moveId.toString()
        );
        setBaseData({
          formData: filteredFormData,
        });

        // if you are editing a previous done form
        if (userData.id?.length) {
          setUserData({
            isFileEdited: BooleanString.TRUE,
            isNewForm: BooleanString.FALSE,
          });
          setLoadingGQL(false);
          // if you are continuing a form
        } else if (userData.firstName) {
          setLoadingGQL(false);
          // else download the data of the user and prefill the form
        } else {
          let currentAddress = {
            ...(fromAddress as Address),
            normalizationResponse: '',
          };

          let newAddress = {
            ...(toAddress as Address),
            normalizationResponse: '',
          };

          // if a previous form has been filled - move verified or not -
          // we need to retrieve addresses submitted in form
          // as they are the addresses that will be set on every form for the current move
          if (typeof filteredFormData?.[0]?.move.need_verify === 'boolean') {
            currentAddress = {
              ...formatRESTAddress(filteredFormData[0].from_address),
              normalizationResponse: '',
            };
            newAddress = {
              ...formatRESTAddress(filteredFormData[0].to_address),
              normalizationResponse: '',
            };

            setUserData({
              addressesNormalized: BooleanString.TRUE,
            });
          }

          const minimumForwardMailDate = addDays(new Date(), 10);
          const maximumForwardMailDate = addMonths(new Date(), 3);
          const forwardMailDate =
            new Date(date) < minimumForwardMailDate
              ? format(minimumForwardMailDate, 'MM/dd/yyyy')
              : new Date(date) > maximumForwardMailDate
              ? format(maximumForwardMailDate, 'MM/dd/yyyy')
              : format(new Date(date), 'MM/dd/yyyy');
          // finally set user data
          setUserData({
            firstName,
            lastName,
            phone: `1${phone}`,
            moveId: moveId.toString(),
            currentAddress,
            newAddress,
            forwardMailDate,
            isFileEdited: BooleanString.FALSE,
            isNewForm:
              // only when need verify exists and is false we are editing the form
              filteredFormData?.[0]?.move.need_verify === false
                ? BooleanString.TRUE
                : BooleanString.FALSE,
          });

          // don't forget to normalize the addresses.
          normalizeAddresses(
            [currentAddress, newAddress],
            setAddressesCallback
          );
        }
      }
    }
    // we don't want any userData depedencies, since we are setting userData.
    // But we do need to check is we have existing data in local storage to set the loading state to null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gqlData, gqlError, loading, loadingGQL, queryLoading]);

  return <>{children}</>;
};
